import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Icon from "./AppIcon";

/**
 * A wrapper component that checks for user authentication AND role authorization.
 * * @param {object} props
 * @param {React.ReactNode} props.children The component to render if authorized.
 * @param {string[]} [props.allowedRoles] An array of roles that are permitted to access this route.
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
    // Ensure your useAuth hook provides the 'profile' object which contains the role.
    const { user, profile, loading } = useAuth();
    const location = useLocation();

    // The user's role is typically stored in the profile fetched from the database
    // which should include the user_metadata role from Supabase.
    const userRole = profile?.user_metadata?.role || profile?.role;

    // --- 1. Handle Loading State ---
    if (loading) {
        // ... (Your loading spinner styles and markup remain the same)
        const styles = {
            overlay: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100%',
                backgroundColor: '#f5f5f5',
            },
            spinner: {
                width: '40px',
                height: '40px',
                border: '10px solid #e0e0e0',
                borderTop: '10px solid #007BFF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px',
                marginTop: '50px',
            },
            text: {
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#333',
                fontFamily: 'Arial, sans-serif',
            },
        };
        return (
            <div style={styles.overlay}>
                <div className="w-350 h-350 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={150} color="white" />
                </div>
                <div style={styles.spinner}></div>
                <div style={styles.text}>Chargement en cours...</div>
            </div>
        );
    }

    // --- 2. Handle Unauthenticated State (Highest Priority Check) ---
    if (!user) {
        const postLogoutRedirect = sessionStorage.getItem('postLogoutRedirect');
        if (postLogoutRedirect) {
            sessionStorage.removeItem('postLogoutRedirect');
            return <Navigate to={postLogoutRedirect} replace />;
        }
        return <Navigate to="/login-authentication" state={{ from: location }} replace />;
    }

    // --- 3. Handle Role Authorization ---

    // If allowedRoles is defined, check if the user's role is included.
    if (user && allowedRoles && allowedRoles.length > 0) {
        // The check converts the userRole to lower case for case-insensitive comparison
        const isAuthorized = allowedRoles.map(r => r.toLowerCase()).includes(userRole?.toLowerCase());

        if (userRole && !isAuthorized) {

            return <Navigate to="/unauthorized" replace />;
        }
    }

    // --- 4. Fully Authorized State ---
    // User is logged in and role check passed (or no role check was required).
    return children;
};

export default ProtectedRoute;